import { SignedUploadRequest, UploadAPIHandler, UploadAsset, UploadPurpose, UploadSession } from '../NetworkClient/BackendAPIs/UploadAPIHandler';

export type UploadTaskStatus = 'QUEUED' | 'UPLOADING' | 'PAUSED' | 'COMPLETING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export type UploadErrorCode = 'AUTHORIZATION_FAILED' | 'NETWORK_ERROR' | 'UPLOAD_FAILED' | 'INVALID_RESPONSE';

export class UploadError extends Error {
  constructor(public readonly code: UploadErrorCode, message: string, public readonly httpStatus?: number) {
    super(message);
    this.name = 'UploadError';
  }
}

export const getUploadErrorMessage = (error: unknown): string => {
  if (error instanceof UploadError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error) return error;
  return 'The file could not be uploaded. Please try again.';
};

class UploadTransportError extends Error {
  constructor(public readonly httpStatus: number, message: string) {
    super(message);
    this.name = 'UploadTransportError';
  }
}

export interface UploadProgress {
  bytesUploaded: number;
  totalBytes: number;
  percentage: number;
  partsCompleted: number;
  totalParts: number;
}

export interface UploadTaskOptions {
  purpose: UploadPurpose;
  resourceId?: string;
  concurrency?: number;
  onProgress?: (progress: UploadProgress) => void;
}

interface CompletedPart {
  partNumber: number;
  entityTag: string;
  sizeBytes: number;
}

export class ResumableUploadTask {
  status: UploadTaskStatus = 'QUEUED';
  private readonly api = UploadAPIHandler.shared;
  private readonly concurrency: number;
  private session?: UploadSession;
  private activeRequests = new Map<number, XMLHttpRequest>();
  private completedParts = new Map<number, CompletedPart>();
  private partProgress = new Map<number, number>();
  private paused = false;

  constructor(private readonly file: File, private readonly options: UploadTaskOptions) {
    this.concurrency = options.concurrency || 3;
  }

  async start(): Promise<UploadAsset> {
    try {
      this.session = await this.createSession();
      return await this.run(this.session);
    } catch (error) {
      return this.handleFailure(error);
    }
  }

  async resume(): Promise<UploadAsset> {
    if (!this.session) throw new Error('Upload session has not been created');
    this.paused = false;
    this.status = 'UPLOADING';
    try {
      this.session = await this.api.resume(this.session.sessionId);
      return await this.run(this.session);
    } catch (error) {
      return this.handleFailure(error);
    }
  }

  async pause(): Promise<void> {
    if (!this.session || this.status !== 'UPLOADING') return;
    this.paused = true;
    this.activeRequests.forEach((request) => request.abort());
    this.activeRequests.clear();
    this.partProgress.clear();
    this.status = 'PAUSED';
    await this.api.pause(this.session.sessionId);
    this.emitProgress();
  }

  async cancel(): Promise<void> {
    if (!this.session) return;
    this.paused = true;
    this.activeRequests.forEach((request) => request.abort());
    this.activeRequests.clear();
    await this.api.abort(this.session.sessionId);
    this.status = 'CANCELLED';
  }

  private async run(session: UploadSession): Promise<UploadAsset> {
    this.status = 'UPLOADING';
    this.completedParts = new Map(session.completedParts.map((partNumber) => [partNumber, { partNumber, entityTag: '', sizeBytes: this.partSizeFor(partNumber, session) }]));
    if (session.strategy === 'SINGLE') {
      if (!session.singleUpload) throw new Error('Single upload session has no signed request');
      try {
        await this.put(session.singleUpload, this.file, 1, false);
      } catch (error) {
        if (!(error instanceof UploadTransportError) || error.httpStatus !== 403) throw error;
        await this.api.abort(session.sessionId).catch(() => {});
        this.session = await this.createSession();
        if (!this.session.singleUpload) throw new UploadError('INVALID_RESPONSE', 'The upload service returned an invalid response. Please try again.');
        await this.put(this.session.singleUpload, this.file, 1, false);
        session = this.session;
      }
    } else {
      await this.uploadMultipart(session);
    }
    if (this.paused) throw new Error('Upload paused');
    this.status = 'COMPLETING';
    const asset = await this.api.complete(session.sessionId);
    this.status = 'COMPLETED';
    return asset;
  }

  private async uploadMultipart(session: UploadSession): Promise<void> {
    const remaining = Array.from({ length: session.totalParts }, (_, index) => index + 1).filter((partNumber) => !this.completedParts.has(partNumber));
    for (let offset = 0; offset < remaining.length && !this.paused; offset += this.concurrency) {
      const batch = remaining.slice(offset, offset + this.concurrency);
      const signedParts = await this.api.signParts(session.sessionId, batch);
      const uploaded = await Promise.all(signedParts.map(async (signed) => ({
        partNumber: signed.partNumber,
        entityTag: await this.putPartWithSignatureRefresh(session, signed),
        sizeBytes: this.partSizeFor(signed.partNumber, session),
      })));
      if (this.paused) return;
      uploaded.forEach((part) => this.completedParts.set(part.partNumber, part));
      await this.api.reportParts(session.sessionId, uploaded);
      this.emitProgress();
    }
  }

  private chunkFor(partNumber: number, session: UploadSession): Blob {
    const start = (partNumber - 1) * session.partSizeBytes;
    return this.file.slice(start, Math.min(start + session.partSizeBytes, this.file.size));
  }

  private partSizeFor(partNumber: number, session: UploadSession): number {
    return this.chunkFor(partNumber, session).size;
  }

  private async putPartWithSignatureRefresh(session: UploadSession, request: SignedUploadRequest & { partNumber: number }): Promise<string> {
    try {
      return await this.put(request, this.chunkFor(request.partNumber, session), request.partNumber, true);
    } catch (error) {
      if (!(error instanceof UploadTransportError) || error.httpStatus !== 403) throw error;
      const refreshed = (await this.api.signParts(session.sessionId, [request.partNumber]))[0];
      return this.put(refreshed, this.chunkFor(request.partNumber, session), request.partNumber, true);
    }
  }

  private put(request: SignedUploadRequest, body: Blob, partNumber: number, requireEntityTag: boolean): Promise<string> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      this.activeRequests.set(partNumber, xhr);
      xhr.open(request.method, request.url);
      Object.entries(request.headers).forEach(([name, value]) => xhr.setRequestHeader(name, value));
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) this.partProgress.set(partNumber, event.loaded);
        this.emitProgress();
      };
      xhr.onload = () => {
        this.activeRequests.delete(partNumber);
        this.partProgress.delete(partNumber);
        if (xhr.status >= 200 && xhr.status < 300) {
          const entityTag = xhr.getResponseHeader('ETag');
          if (requireEntityTag && !entityTag) return reject(new UploadError('INVALID_RESPONSE', 'The storage service returned an invalid response. Please try again.'));
          resolve(entityTag || '');
          return;
        }
        reject(new UploadTransportError(xhr.status, `Storage upload failed with status ${xhr.status}`));
      };
      xhr.onerror = () => { this.activeRequests.delete(partNumber); this.partProgress.delete(partNumber); reject(new UploadError('NETWORK_ERROR', 'The upload was interrupted by a network error. Check your connection and try again.')); };
      xhr.onabort = () => { this.activeRequests.delete(partNumber); this.partProgress.delete(partNumber); reject(new Error('Upload part was aborted')); };
      xhr.send(body);
    });
  }

  private createSession(): Promise<UploadSession> {
    return this.api.createSession({ purpose: this.options.purpose, resourceId: this.options.resourceId, fileName: this.file.name, mimeType: this.file.type || 'application/octet-stream', sizeBytes: this.file.size });
  }

  private async handleFailure(error: unknown): Promise<never> {
    if (!this.paused && this.status !== 'CANCELLED') this.status = 'FAILED';
    if (this.session && !this.paused) {
      await this.api.abort(this.session.sessionId).catch(() => {});
    }
    if (error instanceof UploadError) throw error;
    if (error instanceof UploadTransportError) {
      if (error.httpStatus === 401 || error.httpStatus === 403) {
        throw new UploadError('AUTHORIZATION_FAILED', 'The upload could not be authorized. Please wait a moment and try again. If the problem continues, contact support.', error.httpStatus);
      }
      throw new UploadError('UPLOAD_FAILED', 'The file could not be uploaded. Please try again.', error.httpStatus);
    }
    throw new UploadError('UPLOAD_FAILED', error instanceof Error && error.message ? error.message : 'The file could not be uploaded. Please try again.');
  }

  private emitProgress(): void {
    const completedBytes = Array.from(this.completedParts.values()).reduce((total, part) => total + part.sizeBytes, 0);
    const activeBytes = Array.from(this.partProgress.values()).reduce((total, bytes) => total + bytes, 0);
    const bytesUploaded = completedBytes + activeBytes;
    this.options.onProgress?.({ bytesUploaded, totalBytes: this.file.size, percentage: this.file.size ? Math.min(100, Math.round((bytesUploaded / this.file.size) * 100)) : 0, partsCompleted: this.completedParts.size, totalParts: this.session?.totalParts || 0 });
  }
}